import { ArrowUpRight, Megaphone, X } from "lucide-react"
import { useLocation } from "react-router"
import { RELEASE_LOG_URL, getCurrentBanner } from "~/lib/release-announcements"
import { useReleaseAnnouncement } from "~/hooks/use-release-announcement"
import type { AppFeature } from "~/server/models/appfeature"

// The release bar for the log currently set to `announce: banner` (see
// release-log-entries.ts); nothing shows when no log is. Same shape as the
// pine59.com announcement bar but in the quieter soft-lime tint: a thin strip
// that scrolls away with the page, until closed.
//
// Logged-in Console only: it is rendered by ConsoleLayout's logged-in branch,
// in the TopBars slot under the maintenance bar, inside the content column
// (Console's sidebar is fixed to the top of the screen, so a full-width bar
// would slide under it). TopBars measures the stack, so pages sized to the
// screen shrink by this bar too. Logged-out visitors, on
// the public docs or the login page, never see it. Hidden on the Release Log
// itself, where it would only point at the page in view.
//
// `features` are the account's enabled subscription resources: a log can
// limit its bar to accounts holding one of them (`announce.audience`).
export function ReleaseAnnouncementBanner({
  features,
}: {
  features: readonly AppFeature[]
}) {
  const announcement = getCurrentBanner(features)
  const { pathname } = useLocation()
  const { visible, dismiss } = useReleaseAnnouncement(
    announcement?.slug,
    announcement?.until,
  )

  const shown =
    !!announcement && visible && !pathname.startsWith(RELEASE_LOG_URL)

  if (!shown) {
    return null
  }

  return (
    <section
      aria-label="What's new"
      className="relative border-b border-border bg-accent text-primary"
    >
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-12 py-2.5 text-center">
        <Megaphone
          className="hidden h-4 w-4 shrink-0 text-[#829300] sm:block"
          strokeWidth={2}
          aria-hidden="true"
        />
        <p className="text-sm leading-snug">{announcement.message}</p>
        <a
          // The log's own address: it opens on the log, with its own link preview.
          href={`${RELEASE_LOG_URL}/${announcement.slug}?from=console`}
          // The Release Log is its own page: open it beside Console, not
          // instead of it.
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
        >
          {announcement.linkText}
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </div>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>
  )
}
